import React, { useRef, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Loading } from "@/components/common";
import Layout from "@/components/Layout";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import CalendarPage from "@/pages/CalendarPage";
import Unscheduled from "@/pages/Unscheduled";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Sites from "@/pages/Sites";
import Contracts from "@/pages/Contracts";
import JobTemplates from "@/pages/JobTemplates";
import Consultants from "@/pages/Consultants";
import Leave from "@/pages/Leave";
import Actions from "@/pages/Actions";
import Reports from "@/pages/Reports";
import Documents from "@/pages/Documents";
import Notifications from "@/pages/Notifications";
import Users from "@/pages/Users";
import Audit from "@/pages/Audit";
import Settings from "@/pages/Settings";
import AutoScheduler from "@/pages/AutoScheduler";
import Invoicing from "@/pages/Invoicing";
import Communications from "@/pages/Communications";
import ImportData from "@/pages/ImportData";
import Forms from "@/pages/Forms";
import PlatformLayout from "@/components/PlatformLayout";
import PlatformAccounts from "@/pages/platform/PlatformAccounts";
import PlatformAccountDetail from "@/pages/platform/PlatformAccountDetail";

function AuthCallback() {
  const nav = useNavigate();
  const processed = useRef(false);
  const { setSession } = useAuth();

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", {}, { headers: { "X-Session-ID": sid } });
        setSession(data.token, data.user);
        window.history.replaceState(null, "", "/dashboard");
        nav("/dashboard", { replace: true });
      } catch {
        nav("/login", { replace: true });
      }
    })();
  }, [nav, setSession]);

  return <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]"><Loading label="Signing you in…" /></div>;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]"><Loading /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.is_superadmin) return <Navigate to="/platform/accounts" replace />;
  return children;
}

function PlatformRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-950"><Loading /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_superadmin) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/platform" element={<PlatformRoute><PlatformLayout /></PlatformRoute>}>
        <Route index element={<Navigate to="/platform/accounts" replace />} />
        <Route path="accounts" element={<PlatformAccounts />} />
        <Route path="accounts/:id" element={<PlatformAccountDetail />} />
      </Route>
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/unscheduled" element={<Unscheduled />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/clients/:id" element={<ClientDetail />} />
        <Route path="/sites" element={<Sites />} />
        <Route path="/contracts" element={<Contracts />} />
        <Route path="/job-templates" element={<JobTemplates />} />
        <Route path="/consultants" element={<Consultants />} />
        <Route path="/leave" element={<Leave />} />
        <Route path="/actions" element={<Actions />} />
        <Route path="/auto-scheduler" element={<AutoScheduler />} />
        <Route path="/invoicing" element={<Invoicing />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/communications" element={<Communications />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/users" element={<Users />} />
        <Route path="/audit" element={<Audit />} />
        <Route path="/import" element={<ImportData />} />
        <Route path="/forms" element={<Forms />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
